import { useEffect, useId, useRef, useState } from "react";
import "./Select.css";

/**
 * Arrow-key index maths. Pulled out of the component so it can be tested
 * without a DOM — it's the only branchy logic in here. Wraps at both ends;
 * a current of -1 means "nothing highlighted yet".
 */
export function nextIndex(key, current, length) {
  if (length === 0) return -1;

  switch (key) {
    case "ArrowDown":
      return current < 0 ? 0 : (current + 1) % length;
    case "ArrowUp":
      return current < 0 ? length - 1 : (current - 1 + length) % length;
    case "Home":
      return 0;
    case "End":
      return length - 1;
    default:
      return current;
  }
}

const NAV_KEYS = ["ArrowDown", "ArrowUp", "Home", "End"];
const OPEN_KEYS = ["Enter", " ", "ArrowDown", "ArrowUp"];

/**
 * Dropdown built on the ARIA combobox pattern, replacing a native <select>.
 *
 * A native select's popup is drawn by the OS — that's where the blue highlight
 * comes from — and page CSS cannot reach it. Owning the list is the only way to
 * style it, and owning the list means owning the keyboard and the ARIA too.
 *
 * Focus stays on the trigger the whole time; the highlighted row is pointed at
 * with aria-activedescendant rather than moving DOM focus into the list.
 *
 * @param {object} props
 * @param {string} props.value            currently selected option's value
 * @param {(value: string) => void} props.onChange
 * @param {{value: string, label: string, muted?: boolean}[]} props.options
 *   `muted` marks an option that names the filter's category rather than a value
 *   ("All Cities"). It reads quieter in the list and in the trigger, so a filter
 *   that is set stands out against one that isn't.
 * @param {string} props.label            accessible name, e.g. "Filter by city"
 */
export default function Select({ value, onChange, options, label, className = "" }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  // Arrowing past the panel's edge should scroll the row into view, not hide it.
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const openMenu = () => {
    setActive(selectedIndex < 0 ? 0 : selectedIndex);
    setOpen(true);
  };

  const commit = (i) => {
    if (options[i]) onChange(options[i].value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape" || e.key === "Tab") {
      setOpen(false);
      return;
    }

    if (!open) {
      if (OPEN_KEYS.includes(e.key)) {
        e.preventDefault();
        openMenu();
      }
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      commit(active);
      return;
    }

    if (NAV_KEYS.includes(e.key)) {
      e.preventDefault();
      setActive(nextIndex(e.key, active, options.length));
    }
  };

  return (
    <div className={`fsSelect ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className={`fsSelectTrigger ${selected?.muted ? "isMuted" : ""}`.trim()}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`${id}-list`}
        aria-label={label}
        aria-activedescendant={open && active >= 0 ? `${id}-opt-${active}` : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className="fsSelectValue">{selected ? selected.label : label}</span>

        <svg className="fsSelectChevron" viewBox="0 0 12 8" aria-hidden="true">
          <path
            d="M1 1.5 6 6.5 11 1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <ul
          className="fsSelectPanel"
          id={`${id}-list`}
          role="listbox"
          aria-label={label}
          ref={listRef}
        >
          {options.map((o, i) => (
            <li
              key={o.value}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={o.value === value}
              className={[
                "fsSelectOption",
                i === active && "isActive",
                o.value === value && "isSelected",
                o.muted && "isMuted",
              ]
                .filter(Boolean)
                .join(" ")}
              // mousedown, not click: it fires before the trigger's blur, so the
              // panel is still mounted when we commit.
              onMouseDown={(e) => {
                e.preventDefault();
                commit(i);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span>{o.label}</span>

              {o.value === value && (
                <svg className="fsSelectCheck" viewBox="0 0 12 10" aria-hidden="true">
                  <path
                    d="M1 5 4.5 8.5 11 1.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
